"use client";

import { ArrowLeft, Calendar, Share2, Tag, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-12 bg-muted/40 animate-pulse" />
        <div className="h-96 bg-muted/40 animate-pulse" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/40 animate-pulse" />
          <div className="h-4 bg-muted/40 animate-pulse w-5/6" />
          <div className="h-4 bg-muted/40 animate-pulse w-4/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Impossible de charger l'article.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Article introuvable.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <div className="mb-8">
        <Link
          to="/Blog"
          className="inline-flex items-center gap-2 text-sm font-default uppercase tracking-wider text-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="size-4" />
          Retour au blog
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center px-3 py-1 text-xs font-default uppercase tracking-wider bg-primary text-primary-foreground rounded-md"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default font-medium text-4xl md:text-6xl leading-[1.1] tracking-tight mb-8 text-foreground"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-10 text-sm font-default uppercase tracking-wider text-muted-foreground">
        {post.author && (
          <div className="flex items-center gap-2">
            <User className="size-4 text-primary" />
            <span>{post.author.name}</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-primary" />
            <span>{formattedDate}</span>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="relative mb-12 border border-border shadow-md rounded-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto block aspect-video object-cover rounded-lg"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute -top-3 -left-3 bg-primary text-primary-foreground px-4 py-1 text-xs font-default uppercase tracking-widest font-medium rounded-md">
            Article
          </div>
        </div>
      )}

      {}
      <div
        className="prose max-w-none font-default text-lg md:text-xl leading-relaxed text-foreground/90 [&>p]:mb-6 [&>h2]:font-default [&>h2]:font-medium [&>h2]:text-3xl [&>h2]:mt-12 [&>h2]:mb-4 [&>h3]:font-default [&>h3]:font-medium [&>h3]:text-2xl [&>h3]:mt-8 [&>h3]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-8 [&>a]:text-primary [&>a]:underline [&_img]:my-8"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-default uppercase tracking-wider text-muted-foreground">
              <Tag className="size-4 text-primary" />
              <span>Tags:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, i) => (
                <span
                  data-index={i}
                  key={tag.id}
                  className="inline-flex items-center px-3 py-1 text-xs font-default uppercase tracking-wider border border-border text-foreground rounded-md hover:border-primary hover:text-primary transition-colors"
                >
                  #{tag.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {}
      {post.author && (
        <Card className="bg-card/60 backdrop-blur-sm border border-border shadow-md rounded-lg">
          <CardContent className="p-8">
            <div className="flex items-start gap-2 mb-6">
              <div className="h-px flex-1 bg-border mt-3" />
              <span className="text-xs font-default uppercase tracking-widest text-primary px-3">
                À propos de l'auteure
              </span>
              <div className="h-px flex-1 bg-border mt-3" />
            </div>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <Avatar className="size-24 border-2 border-primary rounded-lg">
                {post.author.avatar && (
                  <AvatarImage
                    src={
                      post.author.avatar["96"] ||
                      Object.values(post.author.avatar)[0]
                    }
                    alt={post.author.name}
                  />
                )}
                <AvatarFallback className="bg-primary text-primary-foreground font-default font-medium text-2xl rounded-lg">
                  {post.author.name?.charAt(0) || "S"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-default font-medium text-2xl mb-2">
                  {post.author.name}
                </h3>
                <p className="font-default text-base text-muted-foreground mb-4 leading-relaxed">
                  Accompagnante postnatal passionnée par le bien-être des mères et des nouveau-nés à Genève et sur La Côte.
                </p>
                {post.author.link && (
                  <Link to="/AboutPage">
                    <Button
                      variant="outline"
                      size="sm"
                      className="font-default uppercase tracking-wider text-xs rounded-md"
                    >
                      En savoir plus
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {}
      <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2 text-xs font-default uppercase tracking-widest text-muted-foreground">
          <Share2 className="size-4 text-primary" />
          Partager cet article
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="font-default uppercase tracking-wider text-xs rounded-md"
          >
            Copier le lien
          </Button>
          <Button
            size="sm"
            className="font-default uppercase tracking-wider text-xs bg-primary text-primary-foreground hover:bg-primary/90 rounded-md"
          >
            S'abonner
          </Button>
        </div>
      </div>
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-72 bg-muted/40 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-muted-foreground font-default text-sm">
        Aucun article lié disponible.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => {
        const dateStr = post.date
          ? new Date(post.date).toLocaleDateString("fr-FR", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "";
        return (
          <Link
            data-index={i}
            key={post.id}
            to={post.link || "#"}
            className="group block"
          >
            <Card className="h-full bg-card/60 backdrop-blur-sm border border-border shadow-md hover:shadow-lg hover:border-primary transition-all duration-300 overflow-hidden rounded-lg">
              {post.featuredImage && (
                <div className="aspect-video overflow-hidden bg-muted">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <CardContent className="p-6">
                <div className="text-xs font-default uppercase tracking-widest text-primary mb-3">
                  {dateStr || "Dernier article"}
                </div>
                <h3
                  className="font-default font-medium text-xl leading-tight mb-3 group-hover:text-primary transition-colors line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: post.title }}
                />
                {post.excerpt && (
                  <div
                    className="font-default text-sm text-muted-foreground line-clamp-3 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />
                )}
                <div className="mt-4 inline-flex items-center gap-2 text-xs font-default uppercase tracking-widest text-foreground group-hover:text-primary transition-colors">
                  Lire la suite
                  <ArrowLeft className="size-3 rotate-180" />
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
  });

  return (
    <section data-section-id="2597"
      id="single-post-template-section"
      className="relative bg-background text-foreground py-20 md:py-28 px-4 md:px-8 overflow-hidden"
    >
      {}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-24 max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs font-default uppercase tracking-widest text-primary mb-3">
                Poursuivre la lecture
              </div>
              <h2 className="font-default font-medium text-3xl md:text-5xl leading-tight tracking-tight">
                Articles connexes
              </h2>
            </div>
            <Link
              to="/Blog"
              className="inline-flex items-center gap-2 text-xs font-default uppercase tracking-widest text-foreground hover:text-primary transition-colors"
            >
              Voir tout
              <ArrowLeft className="size-3 rotate-180" />
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsContent />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}